import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { UXPContext } from '@core/context/Context';
import { hasValue } from '@utils';
import { useNavigate } from 'react-router-dom';
import Button from '@widget/buttons/Button';
import ErrorState from '@helper/ErrorState';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
/** The go-back button is the default action of a full-page error only — it is what keeps
 *  the router dependency in these presets instead of in the shared ErrorState. */
function useGoBackAction(mode, action) {
    const context = React.useContext(UXPContext);
    const navigate = useNavigate();
    if (action)
        return action;
    if (mode !== 'default')
        return null;
    return _jsx(Button, { title: context.$L('uxp-core.text.go-back'), onClick: () => { navigate(-1); }, icon: 'fas chevron-left' });
}
/**
 * @export
 * The standard "access denied" page (a lock icon, a message and Go back). Use it when the user
 * may not see a page or section; `mode="inline"` fits it inside a section or tab.
 *
 * @example
 * ```tsx
 * if (!canView) return <NotAuthorized />;
 * ```
 *
 * @category Feedback
 */
export const NotAuthorized = ({ title, description, action, mode = 'default' }) => {
    const context = React.useContext(UXPContext);
    const resolvedAction = useGoBackAction(mode, action);
    return _jsx(ErrorState, { variant: "error", mode: mode, icon: ['fas', 'lock'], title: hasValue(title) ? title : context.$L('uxp-core.text.access-denied'), description: hasValue(description) ? description : context.$L('uxp-core.text.not-authorized-to-view'), descriptionDisplay: "inline", action: resolvedAction });
};
/**
 * @export
 * The standard "not found" page (a message and Go back). Use it when the record or page the
 * URL points to doesn't exist; `mode="inline"` fits it inside a section or tab.
 *
 * @example
 * ```tsx
 * if (!item) return <NotFound description="This location was deleted." />;
 * ```
 *
 * @category Feedback
 */
export const NotFound = ({ title, description, action, mode = 'default' }) => {
    const context = React.useContext(UXPContext);
    const resolvedAction = useGoBackAction(mode, action);
    return _jsx(ErrorState, { variant: "warning", mode: mode, code: "404", title: hasValue(title) ? title : context.$L('uxp-core.text.page-not-found'), description: hasValue(description) ? description : context.$L('uxp-core.text.page-not-found-description'), descriptionDisplay: "inline", action: resolvedAction });
};
// title and description keys (and an icon) per reason; any reason not listed uses the generic pair
const UNAVAILABLE_TEXT = {
    requiresInternet: { title: 'uxp-core.text.unavailable-offline-title', description: 'uxp-core.text.unavailable-offline-description' },
    desktopOnly: { title: 'uxp-core.text.unavailable-desktop-title', description: 'uxp-core.text.unavailable-desktop-description', icon: 'fas desktop' },
};
export const Unavailable = ({ reason, mode = 'default', action }) => {
    var _a;
    const context = React.useContext(UXPContext);
    const backAction = useGoBackAction(mode, action);
    const text = (_a = UNAVAILABLE_TEXT[reason]) !== null && _a !== void 0 ? _a : { title: 'uxp-core.text.unavailable-title', description: 'uxp-core.text.unavailable-description' };
    // desktopOnly: only the message. A button that navigates would add a history entry, so the
    // phone's own Back would land on the editor again; the phone's Back (or the menu) leaves it.
    const resolvedAction = reason === 'desktopOnly' ? null : backAction;
    return _jsx(ErrorState, { variant: "info", mode: mode, icon: text.icon, title: context.$L(text.title), description: context.$L(text.description), descriptionDisplay: "inline", action: resolvedAction });
};
/**
 * @export
 * Shows its children on larger screens. On phones it shows "This page needs a larger screen"
 * instead. Use it for an editor that opens over a page that works on phones (from a URL
 * parameter, a modal or a panel). Whole pages use the `desktopOnly` page flag in bundle.json.
 *
 * @example
 * if (isMobile && designerKey) return <DesktopOnly />;
 */
export const DesktopOnly = ({ children }) => {
    const isMobile = useIsMobileLayout();
    if (!isMobile)
        return _jsx(_Fragment, { children: children });
    return _jsx(Unavailable, { reason: "desktopOnly" });
};
